import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Clock, MapPin, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { services, PLANITY_URL } from '@/data/services';
import logoSrc from '@/assets/logo-body-by-cc.webp';

const navLinks = [
  { label: 'Accueil', path: '/' },
  { label: 'Soins', path: '/soins', hasDropdown: true },
  { label: 'Formules', path: '/formules' },
  { label: 'Offrir', path: '/offrir' },
  { label: 'À propos', path: '/a-propos' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, []);

  const handleMobileNav = useCallback((path: string) => {
    closeMobile();
    // Use setTimeout to ensure state updates before navigation
    setTimeout(() => {
      navigate(path);
      window.scrollTo(0, 0);
    }, 10);
  }, [closeMobile, navigate]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setServicesOpen(false);
    };
    if (servicesOpen) {
      document.addEventListener('click', handleClick, true);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('click', handleClick, true);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [servicesOpen]);

  // Close desktop dropdown on route change
  useEffect(() => {
    setServicesOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-10 text-xs font-body">
          <div className="flex items-center gap-6">
            <a href="tel:+33645230034" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone size={13} className="text-petrol-foreground/50" />
              06 45 23 00 34
            </a>
            <a href="https://maps.app.goo.gl/MYjhdAwtngvYopSZ9" target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-2 text-petrol-foreground/60 hover:text-petrol-foreground transition-colors">
              <MapPin size={13} className="text-petrol-foreground/50" />
              65 Bd Westinghouse, 93270 Sevran
            </a>
          </div>
          <span className="flex items-center gap-2 text-petrol-foreground/60">
            <Clock size={13} className="text-petrol-foreground/50" />
            Mar-Dim : 10h-20h
          </span>
        </div>
      </div>

      <header className="fixed top-10 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-3">
            <img src={logoSrc} alt="Body by CC" className="h-16 w-auto" width={72} height={112} decoding="async" loading="eager" />
            <div>
              <span className="text-sm font-heading font-semibold text-foreground leading-tight block">Body by CC</span>
              <span className="text-[10px] text-muted-foreground leading-tight block font-body">Institut Santé & Esthétique</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.path} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-colors inline-flex items-center gap-1 ${
                      location.pathname.startsWith('/soins')
                        ? 'text-primary'
                        : 'text-foreground hover:text-primary'
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {servicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-64 rounded-xl border border-border bg-card shadow-lg overflow-hidden"
                      >
                        <Link
                          to="/soins"
                          onClick={() => { setServicesOpen(false); window.scrollTo(0, 0); }}
                          className="block px-4 py-3 text-sm font-medium font-body text-foreground hover:bg-muted/50 transition-colors border-b border-border"
                        >
                          Tous les soins
                        </Link>
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/soins/${s.slug}`}
                            onClick={() => { setServicesOpen(false); window.scrollTo(0, 0); }}
                            className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground font-body hover:bg-muted/50 hover:text-foreground transition-colors"
                          >
                            <s.icon size={15} className="text-primary shrink-0" />
                            {s.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-foreground hover:text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 px-5 py-2 rounded-full text-sm font-medium font-body bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Prendre RDV
            </a>
          </nav>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-0">
            <a
              href="tel:+33645230034"
              className="p-2 text-foreground hover:text-primary transition-colors"
              aria-label="Appeler"
            >
              <Phone size={22} />
            </a>
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 text-foreground"
              aria-label="Menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu - CSS transition instead of framer-motion */}
      <div
        className={`fixed inset-0 z-[999] bg-card flex flex-col transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!mobileOpen}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <button
            onClick={() => handleMobileNav('/')}
            className="flex items-center gap-3"
          >
            <img src={logoSrc} alt="Body by CC" className="h-14 w-auto" width={56} height={56} />
            <span className="text-sm font-heading font-semibold text-foreground">Body by CC</span>
          </button>
          <button onClick={closeMobile} className="p-2 text-foreground" aria-label="Fermer">
            <X size={24} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            link.hasDropdown ? (
              <div key={link.path}>
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className={`w-full flex items-center justify-between py-3 px-4 rounded-lg text-lg font-body transition-colors ${
                    location.pathname.startsWith('/soins')
                      ? 'bg-muted text-primary font-semibold'
                      : 'text-foreground/80 hover:bg-muted/50'
                  }`}
                >
                  {link.label}
                  <ChevronDown size={18} className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-200 ${
                    mobileServicesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <button
                    onClick={() => handleMobileNav('/soins')}
                    className="block w-full text-left py-2 px-8 text-base font-body text-muted-foreground hover:text-primary transition-colors"
                  >
                    Tous les soins
                  </button>
                  {services.map((s) => (
                    <button
                      key={s.slug}
                      onClick={() => handleMobileNav(`/soins/${s.slug}`)}
                      className="flex items-center gap-2 w-full text-left py-2 px-8 text-base font-body text-muted-foreground hover:text-primary transition-colors"
                    >
                      <s.icon size={14} className="text-primary shrink-0" />
                      {s.title}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={link.path}
                onClick={() => handleMobileNav(link.path)}
                className={`py-3 px-4 rounded-lg text-lg font-body transition-colors text-left ${
                  location.pathname === link.path
                    ? 'bg-muted text-primary font-semibold'
                    : 'text-foreground/80 hover:bg-muted/50'
                }`}
              >
                {link.label}
              </button>
            )
          ))}
        </nav>

        <div className="border-t border-border px-6 py-6 flex flex-col gap-3">
          <a
            href="tel:+33645230034"
            className="w-full py-3 rounded-full border border-border text-center font-medium font-body text-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
          >
            <Phone size={16} />
            06 45 23 00 34
          </a>
          <a
            href={PLANITY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3 rounded-full bg-primary text-primary-foreground text-center font-medium font-body hover:bg-primary/90 transition-colors"
          >
            Prendre RDV
          </a>
        </div>
      </div>
    </>
  );
};

export default SiteHeader;